import { useEffect, useMemo, useState } from "react";
import client, { logoUrl, imgUrl } from "@/lib/glossyApi";
import MagazineLayout from "@/components/MagazineLayout";
import { Reveal } from "@/components/Reveal";
import MiniAd from "@/components/MiniAd";
import { Search, User, MapPin, Phone, Mail, Instagram, ArrowUpRight, Sparkles } from "lucide-react";
import { igUrl } from "@/lib/socialLinks";

const BOROUGHS = ["All", "Shoreditch", "Hackney", "Peckham", "Deptford", "Marylebone", "Mayfair", "Camden", "Brixton"];

function normUrl(u) {
  if (!u) return null;
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

export default function Directory() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [borough, setBorough] = useState("All");

  useEffect(() => {
    document.title = "The Directory — S/ME";
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (borough !== "All") qs.set("borough", borough);
    client.get(`/members?${qs.toString()}`).then((r) => setMembers(r.data)).catch(() => {});
  }, [q, borough]);

  // randomise the running order on each refresh
  const ordered = useMemo(() => [...members].sort(() => Math.random() - 0.5), [members]);

  return (
    <MagazineLayout>
      <div className="pt-[68px] bg-cream min-h-screen">
        <header className="px-5 sm:px-8 pt-16 sm:pt-24 pb-10 max-w-[1200px] mx-auto">
          <p className="overline text-gloss-silver mb-4">The Members</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-none">The Directory</h1>
          <p className="font-sans text-lg text-ink/70 mt-5 max-w-xl">The UK's most stylish small businesses — find them, and get in touch.</p>
          <div className="mt-5 max-w-xl border border-gloss-gold/50 bg-gloss-gold/10 px-4 py-3 flex items-start gap-3" data-testid="directory-concierge-note">
            <Sparkles size={16} className="text-gloss-gold shrink-0 mt-0.5" />
            <p className="font-sans text-sm text-ink/75 leading-relaxed">
              <b className="text-ink">Not sure who you need?</b> Tap <b className="text-ink">Ask S/ME</b> in the corner —
              tell our concierge the job and it points you to the right member. It only ever recommends the businesses on this page.
            </p>
          </div>

          <div className="mt-10 flex flex-col gap-6">
            <div className="relative w-full max-w-md">
              <Search size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-ink/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" className="w-full bg-transparent border-b border-ink/30 py-3 pl-7 font-sans text-lg focus:outline-none focus:border-gloss-silver transition-colors" data-testid="member-search" />
            </div>
            <div className="flex flex-wrap gap-2.5">
              {BOROUGHS.map((b) => (
                <button key={b} onClick={() => setBorough(b)} className={`font-mono text-xs uppercase tracking-wider px-3.5 py-2 border transition-colors ${borough === b ? "bg-ink text-cream border-ink" : "border-ink/30 hover:border-ink"}`} data-testid={`borough-${b.toLowerCase()}`}>{b}</button>
              ))}
            </div>
          </div>
        </header>

        <section className="px-5 sm:px-8 pb-28 max-w-[1200px] mx-auto">
          <div className="mb-12"><MiniAd variant="banner" mode="image" space="directory_banner" /></div>
          {ordered.length === 0 ? (
            <p className="font-sans text-ink/50 py-20 text-center">No members match your search yet.</p>
          ) : (
            <div className="border-t border-ink/15" data-testid="directory-list">
              {ordered.map((m, i) => {
                const site = normUrl(m.website);
                const shop = normUrl(m.shop_url);
                const showSite = site && (!shop || site.replace(/\/+$/, "") !== shop.replace(/\/+$/, ""));
                const phone = m.display_phone === false ? null : (m.mobile || m.landline || null);
                const ig = igUrl(m.instagram);
                const logo = logoUrl(m);
                return (
                  <Reveal key={m.id} delay={(i % 4) * 0.04}>
                    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6 sm:gap-10 py-10 border-b border-ink/15" data-testid="directory-row">
                      <div className="aspect-square bg-secondary border border-ink/10 flex items-center justify-center overflow-hidden p-6" data-testid="directory-logo">
                        {logo ? (
                          <img src={logo} alt={m.business_name} className="max-h-[70%] max-w-[80%] object-contain" />
                        ) : (
                          <span className="font-serif tracking-tight text-2xl sm:text-3xl text-ink/70 text-center leading-tight uppercase">{m.business_name}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-none">{m.business_name}</h2>
                        <p className="font-serif italic text-lg text-ink/60 mt-1">{m.business_category || m.category}</p>
                        <div className="mt-5 space-y-2.5 font-sans text-base text-ink/80">
                          {m.owner_name && <p className="flex items-center gap-3"><User size={16} className="text-ink/40 shrink-0" /> {m.owner_name}</p>}
                          {(m.address || m.borough) && <p className="flex items-center gap-3"><MapPin size={16} className="text-ink/40 shrink-0" /> {m.address || m.borough}</p>}
                          {phone && <p className="flex items-center gap-3"><Phone size={16} className="text-ink/40 shrink-0" /> <a href={`tel:${phone}`} className="hover:text-gloss-gold transition-colors">{phone}</a></p>}
                          {m.email && <p className="flex items-center gap-3"><Mail size={16} className="text-ink/40 shrink-0" /> <a href={`mailto:${m.email}`} className="hover:text-gloss-gold transition-colors">{m.email}</a></p>}
                        </div>
                        <div className="mt-6 flex items-center gap-6">
                          {shop && <a href={shop} target="_blank" rel="noreferrer" className="overline link-underline flex items-center gap-1.5" data-testid="directory-shop">Shop <ArrowUpRight size={13} /></a>}
                          {showSite && <a href={site} target="_blank" rel="noreferrer" className="overline link-underline flex items-center gap-1.5" data-testid="directory-website">Website <ArrowUpRight size={13} /></a>}
                          {ig && <a href={ig} target="_blank" rel="noreferrer" className="text-ink/60 hover:text-ink transition-colors" aria-label="Instagram" data-testid="directory-instagram"><Instagram size={18} /></a>}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </MagazineLayout>
  );
}
